/**
 * Farming: what the hoe may plough, what seed may be sown into, and crops
 * growing.
 *
 * Only chunks the player has modified are scanned. That is not an
 * optimisation trick but a consequence of how the world works: crops only
 * exist on farmland, farmland only exists where someone ploughed, and
 * ploughing marks a chunk modified. A field is always in that set, and the set
 * is small - a handful of chunks against the world's two hundred.
 *
 * The alternative, Minecraft's random ticks across every loaded chunk, spends
 * almost all of its work on cells that could never contain a crop.
 *
 * Pure logic over a World: no timers, no engine. The caller decides when a
 * pass happens, which is what makes growth testable without waiting.
 */

import type { World } from "./world.js";
import { CHUNK_SIZE, blockIndex } from "./chunk.js";
import { MAX_LIGHT } from "./light.js";
import {
  BlockId, isCrop, isCrop as _isCrop, nextCropStage,
  isTillable, isFarmland, isFluid, needsSupport, isSapling,
} from "../block/registry.js";

/** Seconds of real time between growth passes. */
/**
 * Why an action on a block was refused.
 *
 * Returned rather than a bare boolean so the game can say *why* nothing
 * happened. A tool that silently does nothing is the worst kind of feedback:
 * the player tries the same thing again and concludes the game is broken.
 */
export type FarmAction =
  | "ok"
  | "not-from-above"
  | "not-soil"
  | "not-farmland"
  | "underwater"
  | "occupied";

/**
 * May the hoe plough this block?
 *
 * `fromAbove` is whether the aiming ray struck the top face. Hoeing a wall
 * makes no sense, and allowing it means ploughing cliffs.
 *
 * Refusing underwater is not fussiness. The aiming ray passes through water on
 * purpose, so it can reach a lake bed; without this check the hoe reached
 * straight through the sea, deleted the water block above the seabed and left
 * farmland in a pocket of air that refilled the moment anything else was
 * broken. From the player's side, freshly ploughed soil turned into water.
 */
export function canTill(
  world: World, x: number, y: number, z: number, fromAbove: boolean,
): FarmAction {
  if (!fromAbove) return "not-from-above";
  if (!isTillable(world.getBlock(x, y, z))) return "not-soil";

  const above = world.getBlock(x, y + 1, z);
  if (isFluid(above)) return "underwater";
  // A plant standing on it is knocked off; anything solid is in the way.
  if (above !== BlockId.Air && !needsSupport(above)) return "occupied";
  return "ok";
}

/** May seed be sown here? */
export function canPlant(
  world: World, x: number, y: number, z: number, fromAbove: boolean,
): FarmAction {
  if (!fromAbove) return "not-from-above";
  if (!isFarmland(world.getBlock(x, y, z))) return "not-farmland";
  if (world.getBlock(x, y + 1, z) !== BlockId.Air) return "occupied";
  return "ok";
}

export const GROWTH_PASS_SECONDS = 2;

/**
 * Chance a given crop advances on a given pass.
 *
 * 0.05 every 2 seconds is one stage per 40 seconds on average, so sowing to
 * harvest runs about 80 seconds - roughly a fourteenth of the 20-minute day.
 * Long enough that a field is something you come back to, short enough that
 * you can watch it happen.
 */
export const GROWTH_CHANCE = 0.05;

export interface GrowthResult {
  /** Crops that advanced a stage this pass. */
  grown: number;
  /** Crops that were ready to grow but stood on dry soil. */
  thirsty: number;
}

/**
 * Advance crops one pass.
 *
 * `random` is injectable so tests can force growth instead of waiting for it.
 * Growth is not reproducible from a seed and does not need to be: the result
 * is written into blocks, which are what the save file stores.
 */
export function growCrops(
  world: World,
  random: () => number = Math.random,
  chance: number = GROWTH_CHANCE,
): GrowthResult {
  let grown = 0;
  let thirsty = 0;

  for (const chunk of world.modifiedChunks()) {
    const ox = chunk.cx * CHUNK_SIZE;
    const oy = chunk.cy * CHUNK_SIZE;
    const oz = chunk.cz * CHUNK_SIZE;

    for (let y = 0; y < CHUNK_SIZE; y++) {
      for (let z = 0; z < CHUNK_SIZE; z++) {
        for (let x = 0; x < CHUNK_SIZE; x++) {
          const id = chunk.blocks[blockIndex(x, y, z)]!;
          if (!isCrop(id)) continue;

          const next = nextCropStage(id);
          if (next === 0) continue; // already ripe

          // Wheat needs water. Dry soil holds a crop where it is rather than
          // killing it, so a field is recoverable by digging a channel.
          if (world.getBlock(ox + x, oy + y - 1, oz + z) !== BlockId.FarmlandWet) {
            thirsty++;
            continue;
          }

          if (random() >= chance) continue;
          world.setBlock(ox + x, oy + y, oz + z, next);
          grown++;
        }
      }
    }
  }

  return { grown, thirsty };
}

/**
 * Chance a паросток becomes a tree on a given pass.
 *
 * Much slower than a crop: 0.008 every 2 seconds is about four minutes on
 * average, a fifth of the day. A tree you plant and then watch come up while
 * you stand there is not a tree, and the point of planting one is that the
 * wood is there when you come back.
 */
export const SAPLING_CHANCE = 0.008;

/**
 * How much clear air a паросток needs over its head before it will come up.
 *
 * An acacia runs to nine blocks of trunk plus its crown, so this is what the
 * taller of the two needs. Short of it the sprout simply waits, rather than
 * growing a tree with its top sheared off inside somebody's ceiling.
 */
export const SAPLING_HEADROOM = 11;

export interface SaplingResult {
  /** Паростки that became trees this pass. */
  grown: number;
  /** Ones that were ready but had no room over their heads. */
  cramped: number;
}

/**
 * Advance паростки one pass.
 *
 * Same scan as growCrops and for the same reason: a паросток only exists where
 * a player put one or broke the leaves it fell from, and both mark the chunk
 * modified.
 */
/** How often a lit block of earth greens over, per pass. */
export const GRASS_CHANCE = 0.25;

/**
 * Earth under the open sky greens over.
 *
 * The other half of grass dropping earth: dig a green block and you are
 * holding soil, put it back and it is bare - and then the sun gets at it and
 * it is a lawn again. Without this, the first hole anybody dug left a brown
 * scar in the ground for the rest of the world's life.
 *
 * Only where the sky actually reaches: earth under a floor, in a tunnel or
 * beneath a tree stays earth, which is what makes a cellar a cellar. Sky
 * light rather than daylight, so it does not un-green itself every night.
 *
 * Walked over the chunks somebody has changed, the same as the crops and the
 * saplings and for the same reason: bare earth under the sky only exists
 * where a person put it.
 */
export function spreadGrass(
  world: World,
  random: () => number = Math.random,
  chance: number = GRASS_CHANCE,
): number {
  const waiting: { x: number; y: number; z: number }[] = [];

  for (const chunk of world.modifiedChunks()) {
    const ox = chunk.cx * CHUNK_SIZE;
    const oy = chunk.cy * CHUNK_SIZE;
    const oz = chunk.cz * CHUNK_SIZE;
    for (let y = 0; y < CHUNK_SIZE; y++) {
      for (let z = 0; z < CHUNK_SIZE; z++) {
        for (let x = 0; x < CHUNK_SIZE; x++) {
          if (chunk.blocks[blockIndex(x, y, z)] !== BlockId.Dirt) continue;
          waiting.push({ x: ox + x, y: oy + y, z: oz + z });
        }
      }
    }
  }

  let greened = 0;
  for (const at of waiting) {
    // Something on top of it is a floor, a path or a tree, and none of those
    // grows a lawn underneath itself.
    if (world.getBlock(at.x, at.y + 1, at.z) !== BlockId.Air) continue;
    // Full daylight overhead, which is the sky rather than the hour.
    if (world.light.sky(at.x, at.y + 1, at.z) < MAX_LIGHT) continue;
    if (random() > chance) continue;
    world.setBlock(at.x, at.y, at.z, BlockId.Grass);
    greened++;
  }
  return greened;
}

export function growSaplings(
  world: World,
  random: () => number = Math.random,
  chance: number = SAPLING_CHANCE,
): SaplingResult {
  let grown = 0;
  let cramped = 0;

  // Collected first and grown afterwards: raising a tree writes blocks into
  // the very chunks being walked, and a crown that lands in an unvisited one
  // would otherwise be scanned as if it had always been there.
  const waiting: { x: number; y: number; z: number; id: number }[] = [];

  for (const chunk of world.modifiedChunks()) {
    const ox = chunk.cx * CHUNK_SIZE;
    const oy = chunk.cy * CHUNK_SIZE;
    const oz = chunk.cz * CHUNK_SIZE;

    for (let y = 0; y < CHUNK_SIZE; y++) {
      for (let z = 0; z < CHUNK_SIZE; z++) {
        for (let x = 0; x < CHUNK_SIZE; x++) {
          const id = chunk.blocks[blockIndex(x, y, z)]!;
          if (!isSapling(id)) continue;
          waiting.push({ x: ox + x, y: oy + y, z: oz + z, id });
        }
      }
    }
  }

  for (const sprout of waiting) {
    const { x, y, z, id } = sprout;
    // It may already have been swallowed by a crown raised a moment ago.
    if (world.getBlock(x, y, z) !== id) continue;
    // Soil under it. Farmland does not count: a tree is not a crop, and
    // letting one come up out of a ploughed field would eat the field.
    if (!isTillable(world.getBlock(x, y - 1, z))) continue;

    if (random() >= chance) continue;

    let clear = true;
    for (let dy = 1; dy <= SAPLING_HEADROOM && clear; dy++) {
      if (world.getBlock(x, y + dy, z) !== BlockId.Air) clear = false;
    }
    if (!clear) {
      cramped++;
      continue;
    }

    // The sprout goes first: the trunk starts in the cell it is standing in,
    // and treeBlock refuses to write over anything that is not air.
    world.setBlock(x, y, z, BlockId.Air);
    const raised = world.growTree(x, y - 1, z,
      id === BlockId.OakSapling ? "oak"
      : id === BlockId.PineSapling ? "pine"
      : "acacia",
    );
    if (raised) grown++;
    // A tree that would not fit puts the sprout back rather than eating it.
    else world.setBlock(x, y, z, id);
  }

  return { grown, cramped };
}
